import { makeVar } from '@apollo/client';

/**
 * ТЗ 0119 — тема ERP. Выбор пользователя: «как в системе», светлая или
 * тёмная. По умолчанию — как в системе (prefers-color-scheme), как у
 * Perplexity. Выбор живёт в localStorage этого браузера.
 *
 * Атрибут <html data-theme> ставится ещё до бандла (public/index.html —
 * тот же ключ и та же логика), иначе тёмная тема мигала бы белым при
 * загрузке. Здесь — переключение на лету и слежение за сменой темы ОС.
 */
export type ThemeMode = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const MODES: ThemeMode[] = ['system', 'light', 'dark'];
const STORAGE_KEY = 'appTheme';

const media = (): MediaQueryList | null =>
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

/** Выбор при загрузке: localStorage → «как в системе». */
export const getInitialThemeMode = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && MODES.includes(stored as ThemeMode)) {
      return stored as ThemeMode;
    }
  } catch {
    // приватный режим/запрет хранилища — просто «как в системе»
  }
  return 'system';
};

/** Тема, которую реально видно: «как в системе» → светлая или тёмная. */
export const resolveTheme = (mode: ThemeMode): ResolvedTheme => {
  if (mode !== 'system') return mode;
  return media()?.matches ? 'dark' : 'light';
};

/** Выбор пользователя (реактивно). */
export const themeModeVar = makeVar<ThemeMode>(getInitialThemeMode());

/** Видимая тема (реактивно) — для графиков и всего, что рисует не CSS. */
export const resolvedThemeVar = makeVar<ResolvedTheme>(
  resolveTheme(themeModeVar()),
);

const apply = (mode: ThemeMode) => {
  const resolved = resolveTheme(mode);
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = resolved;
  }
  if (resolvedThemeVar() !== resolved) resolvedThemeVar(resolved);
};

/** Выбрать тему: применить сразу и запомнить. */
export const setThemeMode = (mode: ThemeMode): void => {
  themeModeVar(mode);
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // не запомнится — но применится
  }
  apply(mode);
};

let watching = false;

/**
 * Один раз при старте (src/index.tsx): применить выбор и следить за темой
 * ОС — в режиме «как в системе» ERP меняется вместе с macOS/Windows.
 */
export const initTheme = (): void => {
  apply(themeModeVar());
  if (watching) return;
  watching = true;
  const mq = media();
  const onChange = () => {
    if (themeModeVar() === 'system') apply('system');
  };
  mq?.addEventListener?.('change', onChange);
};
